import { PDFDocument, StandardFonts } from 'pdf-lib';
import { buildFields } from './ttcc-pdf-coordinates';
import { UserSchema } from '@/features/users';
import { CreateOfferFormFields } from '../components';
import { normalizeText } from './normalize-text';

export type TriggerFileDownloadProps = {
  url: string;
  filename: string;
  data: CreateOfferFormFields;
  user: UserSchema;
  name: string;
  technologies: string;
  installedCapacity: string;
  saleOptions: string;
  developmentStatus: string;
  location: string | null;
  corporateFileTitle: string;
  eoiFileTitle: string;
  fileTitle: string;
};

export async function triggerFileDownload(fileProps: TriggerFileDownloadProps) {
  const { url, filename } = fileProps;
  const existingPdfBytes = await fetch(url).then((res) => res.arrayBuffer());
  const pdfDoc = await PDFDocument.load(existingPdfBytes);

  const logoPath = '/images/logo-slogan-green.png';
  const imageBytes = await fetch(logoPath).then((res) => res.arrayBuffer());
  const image = await pdfDoc.embedPng(imageBytes);

  const pages = pdfDoc.getPages();
  const firstPage = pages[0];
  const { width, height } = firstPage.getSize();
  const eoiPage = pdfDoc.insertPage(0, [width, height]);

  const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const emojiFont = await pdfDoc.embedFont(StandardFonts.ZapfDingbats);

  const submissionTimestamp: Date = new Date(Date.now());
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'Europe/Madrid',
  };
  const formattedDate: string = submissionTimestamp.toLocaleString('es-ES', options);

  const fields = buildFields({ fileProps, formattedDate, fontBold, helveticaFont, emojiFont });

  let posY = height - 50; // initial position
  let currentPage = eoiPage;
  let createdPagesCount = 1; // Page counter created (including eoiPage)

  function drawTextBlock(field: any, isOriginalYFixed: boolean) {
    const x = field.x;
    const size = field.size;
    const font = field.font;
    const maxWidth = field.maxWidth || 500;
    const lineHeight = field.lineHeight || size + 2;
    const marginAfter = field.marginAfter || 8;

    let y = field.y !== undefined ? field.y : posY;

    // If it has a fixed Y ORIGINAL, use eoiPage; otherwise, use currentPage
    let targetPage = isOriginalYFixed ? eoiPage : currentPage;

    const text = normalizeText(field.text || '');
    const words = text.replace(/\n/g, ' ').split(/\s+/);
    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const lineWidth = font.widthOfTextAtSize(testLine, size);

      if (lineWidth > maxWidth) {
        // If there's no space on the page, create a new one
        if (y - lineHeight < 50) {
          // Insert the new page right after the created pages and before the original PDF
          currentPage = pdfDoc.insertPage(createdPagesCount, [width, height]);
          createdPagesCount++;
          targetPage = currentPage;
          y = height - 50;
          posY = height - 50;
        }
        targetPage.drawText(currentLine, { x, y, font, size });
        y -= lineHeight;
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }

    if (currentLine) {
      if (y - lineHeight < 50) {
        currentPage = pdfDoc.insertPage(createdPagesCount, [width, height]);
        createdPagesCount++;
        targetPage = currentPage;
        y = height - 50;
        posY = height - 50;
      }
      targetPage.drawText(currentLine, { x, y, font, size });
      y -= lineHeight;
    }

    // Update posY only if the field does NOT have a fixed Y
    const finalY = y - marginAfter;
    if (field.y === undefined) {
      posY = finalY;
    }

    // Return the final Y reached to use in the main loop
    return finalY;
  }

  let lastRowY: any = null; // Save the Y of the current row
  let lowestYInRow: any = null; // The lowest Y reached in the current row

  fields.forEach((field) => {
    const hasOriginalY = field.y !== undefined; // Save if it had original Y
    let fieldToProcess = { ...field }; // Always make a copy

    // If the field has sameLine
    if ((field as any).sameLine) {
      if (lastRowY === null) {
        // It's the first field of the row: set the initial Y
        if (field.y === undefined) {
          fieldToProcess.y = posY; // Use current posY
          lastRowY = posY;
        } else {
          lastRowY = field.y;
        }
      } else {
        // Not the first field: use the Y of the row
        if (field.y === undefined) {
          fieldToProcess.y = lastRowY;
        }
      }
    }

    const finalY = drawTextBlock(fieldToProcess, hasOriginalY);

    // Track the lowest Y in this row
    if ((field as any).sameLine || lastRowY !== null) {
      lowestYInRow = lowestYInRow === null ? finalY : Math.min(lowestYInRow, finalY);
    }

    // If the field does NOT have sameLine, end the row
    if (!(field as any).sameLine) {
      if (lastRowY !== null) {
        // Update posY with the lowest Y of the entire row
        posY = lowestYInRow !== null ? lowestYInRow : finalY;
      } else if (fieldToProcess.y !== undefined && hasOriginalY) {
        // Field with fixed Y that is not part of a multi-field row
        posY = Math.min(posY, finalY);
      } else {
        // Field without Y and without sameLine
        posY = finalY;
      }
      lastRowY = null;
      lowestYInRow = null;
    }
  });

  const pngDims = image.scale(0.1);
  eoiPage.drawImage(image, {
    x: width - pngDims.width - 40,
    y: height - pngDims.height - 40,
    width: pngDims.width,
    height: pngDims.height,
  });

  const pdfBytes = await pdfDoc.save();
  const pdfArrayBuffer = new Uint8Array(pdfBytes).buffer;
  const blob = new Blob([pdfArrayBuffer], { type: 'application/pdf' });

  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();

  // Convert blob to File and return it to use it for download
  const file = new File([blob], filename, { type: 'application/pdf' });
  return file;
}
